import { FC, useEffect, useState } from 'react'
import { AuditEventDetail, getAuditEvent } from '../../../services/auditLogService'
import { AuditActionBadge } from './AuditActionBadge'
import { DiffTable } from './DiffTable'

const initials = (name: string) => {
  const parts = name.trim().split(/\s+/)
  const chars = parts.slice(0, 2).map((p) => p[0]).join('')
  return chars.toUpperCase() || '·'
}

const formatTimestamp = (iso: string) => {
  try {
    const d = new Date(iso)
    return d.toLocaleString(undefined, {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      fractionalSecondDigits: 3 as any,
    })
  } catch {
    return iso
  }
}

interface Props {
  eventId: number
  onClose: () => void
}

export const AuditEventDrawer: FC<Props> = ({ eventId, onClose }) => {
  const [event, setEvent] = useState<AuditEventDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    getAuditEvent(eventId)
      .then((data) => { if (alive) setEvent(data) })
      .catch((e) => { if (alive) setError(e?.message || 'Could not load event.') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [eventId])

  return (
    <>
      <div className="nb-audit-drawer__backdrop" onClick={onClose} />
      <aside className="nb-audit-drawer" role="dialog" aria-modal="true">
        <div className="nb-audit-drawer__header">
          <div className="nb-audit-drawer__title">Event #{eventId}</div>
          <button
            type="button"
            className="nb-audit-drawer__close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="nb-audit-drawer__body">
          {loading && <p className="nb-audit-drawer__row-label">Loading…</p>}
          {error && !loading && <p className="nb-audit-drawer__row-label">{error}</p>}
          {event && !loading && (
            <>
              <section className="nb-audit-drawer__block">
                <div className="nb-audit-drawer__block-title">When / Where</div>
                <div className="nb-audit-drawer__row">
                  <span className="nb-audit-drawer__row-label">Timestamp</span>
                  <span className="nb-audit-drawer__row-value">{formatTimestamp(event.createdAt)}</span>
                </div>
                <div className="nb-audit-drawer__row">
                  <span className="nb-audit-drawer__row-label">IP</span>
                  <span className="nb-audit-drawer__row-value">{event.ip || '—'}</span>
                </div>
                <div className="nb-audit-drawer__row">
                  <span className="nb-audit-drawer__row-label">User agent</span>
                  <span className="nb-audit-drawer__row-value">{event.userAgent || '—'}</span>
                </div>
                {event.requestId && (
                  <div className="nb-audit-drawer__row">
                    <span className="nb-audit-drawer__row-label">Request id</span>
                    <span className="nb-audit-drawer__row-value">{event.requestId}</span>
                  </div>
                )}
              </section>

              <section className="nb-audit-drawer__block">
                <div className="nb-audit-drawer__block-title">Actor</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span
                    className="nb-audit-drawer__row-value nb-audit__avatar"
                    style={{ background: event.actor.avatarUrl ? `url(${event.actor.avatarUrl})` : '#C9C9C9' }}
                  >
                    {!event.actor.avatarUrl && initials(event.actor.name)}
                  </span>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span className="nb-audit-drawer__row-value" style={{ fontFamily: "'Satoshi', sans-serif" }}>
                      {event.actor.name}
                    </span>
                    <span className="nb-audit-drawer__row-label">
                      {event.actor.email || 'system'}{event.actor.role ? ` · ${event.actor.role}` : ''}
                    </span>
                  </div>
                </div>
              </section>

              <section className="nb-audit-drawer__block">
                <div className="nb-audit-drawer__block-title">Target</div>
                <div className="nb-audit-drawer__row">
                  <span className="nb-audit-drawer__row-label">Entity</span>
                  <span className="nb-audit-drawer__row-value">{event.target.entityType || '—'}</span>
                </div>
                <div className="nb-audit-drawer__row">
                  <span className="nb-audit-drawer__row-label">Label</span>
                  <span className="nb-audit-drawer__row-value" style={{ fontFamily: "'Satoshi', sans-serif" }}>
                    {event.target.label || '—'}
                  </span>
                </div>
                {event.target.entityId != null && (
                  <div className="nb-audit-drawer__row">
                    <span className="nb-audit-drawer__row-label">Id</span>
                    <span className="nb-audit-drawer__row-value">#{event.target.entityId}</span>
                  </div>
                )}
              </section>

              <section className="nb-audit-drawer__block">
                <div className="nb-audit-drawer__block-title">
                  Changes <AuditActionBadge action={event.action} />
                </div>
                <DiffTable action={event.action} changes={event.changes as any} />
              </section>
            </>
          )}
        </div>
      </aside>
    </>
  )
}
